import { useCallback, useMemo } from "react";

import type {
  Location,
  SelectionRange,
} from "metabase/query_builder/components/NativeQueryEditor/types";
import * as Lib from "metabase-lib";
import type Question from "metabase-lib/v1/Question";
import type { NativeQuerySnippet } from "metabase-types/api";

const EMPTY_SELECTION_RANGE: SelectionRange = {
  start: { row: 0, column: 0 },
  end: { row: 0, column: 0 },
};

/** Convert an editor row/column into an offset into the raw SQL text. */
function locationToPosition(text: string, location: Location): number {
  const lines = text.split("\n");
  return lines.reduce((acc, line, index) => {
    if (index < location.row) {
      return acc + line.length + 1;
    }
    return acc;
  }, location.column);
}

function getSelectionPositions(text: string, selectionRange: SelectionRange[]) {
  const range = selectionRange[0] ?? EMPTY_SELECTION_RANGE;
  return {
    start: locationToPosition(text, range.start),
    end: locationToPosition(text, range.end),
  };
}

/**
 * The SQL currently selected in the editor, or `""` when nothing is selected.
 *
 * Running a tab prefers this over the whole editor contents, which is how
 * "select a statement and hit run" works.
 */
export function useSelectedText(
  question: Question,
  selectionRange: SelectionRange[],
): string {
  return useMemo(() => {
    const text = Lib.rawNativeQuery(question.query()) ?? "";
    const { start, end } = getSelectionPositions(text, selectionRange);
    return text.slice(start, end);
  }, [question, selectionRange]);
}

/** Replace the current selection with a `{{snippet: …}}` reference. */
export function useInsertSnippetHandler({
  question,
  selectionRange,
  onChange,
}: {
  question: Question;
  selectionRange: SelectionRange[];
  onChange: (question: Question) => void;
}) {
  return useCallback(
    (snippet: NativeQuerySnippet) => {
      const query = question.query();
      const text = Lib.rawNativeQuery(query) ?? "";
      const { start, end } = getSelectionPositions(text, selectionRange);
      const newText = `${text.slice(0, start)}{{snippet: ${snippet.name}}}${text.slice(end)}`;
      onChange(question.setQuery(Lib.withNativeQuery(query, newText)));
    },
    [onChange, question, selectionRange],
  );
}
